import axios from 'axios';
import logger from '@/app/logger';
import { addCampaign, getCampaigns } from '@/core/api/files/files';

// TODO: type this — Vuex module context
/* eslint-disable @typescript-eslint/no-explicit-any */
type Ctx = any;
/* eslint-enable @typescript-eslint/no-explicit-any */

interface Campaign {
    id?: string;
    imageUrl?: string;
    [key: string]: unknown;
}

interface CampaignState {
    campaigns: Campaign[];
    activeCampaign: Campaign;
    displayEditModal: boolean;
    isLoading: boolean;
}

const CampaignStore = {
    namespaced: true,
    state: {
        campaigns: [] as Campaign[],
        activeCampaign: {} as Campaign,
        displayEditModal: false,
        isLoading: false,
    },
    mutations: {
        updateCampaigns(state: CampaignState, campaigns: Campaign[]): void {
            state.campaigns = [...campaigns];
        },
        updateActiveCampaign(state: CampaignState, campaign: Campaign): void {
            state.activeCampaign = campaign;
        },
        toggleDisplayEditModal(state: CampaignState): void {
            state.displayEditModal = !state.displayEditModal;
        },
    },
    actions: {
        async addCampaign({ dispatch }: Ctx, event: any): Promise<void> {
            const files = event && event.target && event.target.files;
            try {
                await addCampaign(files);
                dispatch('getCampaigns');
            } catch (err: unknown) {
                logger.error(err);
            }
        },
        async getCampaigns({ commit }: Ctx): Promise<void> {
            const result = await getCampaigns();
            commit('updateCampaigns', result);
        },
        async getActiveCampaign({ commit, dispatch }: Ctx, campaignId: string): Promise<void> {
            return axios
                .get(`/api/campaigns/${campaignId}`)
                .then((result) => {
                    commit('updateActiveCampaign', result.data);
                })
                .catch((error: unknown) => {
                    dispatch('errors/modal', error, { root: true });
                });
        },
        async submitImage(
            { state, dispatch, commit }: Ctx,
            { file, campaign }: { file: File; campaign: Campaign },
        ): Promise<string> {
            state.isLoading = true;
            const data = new FormData();
            data.append('files', file);
            let currentFile: any = file;
            try {
                const resp = await axios.post(`/api/files?path=${'campaign'}`, data, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
                if (resp) {
                    campaign.imageUrl = resp.data.files[0].url;
                    dispatch(
                        'analytics/pushEvent',
                        {
                            event: {
                                category: 'Campaign',
                                action: 'AddImage',
                                name: campaign.imageUrl,
                                value: campaign.id,
                            },
                        },
                        { root: true },
                    );
                    commit('updateActiveCampaign', campaign);
                }
            } catch (error) {
                campaign.imageUrl = '';
                currentFile = '';
                const err = error as any;
                if (err.response && err.response.status === 413) {
                    dispatch('errors/modal', new Error('File size is too large (should be 32MB or smaller)'), {
                        root: true,
                    });
                } else {
                    dispatch('errors/modal', error, { root: true });
                }
            }
            state.isLoading = false;
            return campaign.imageUrl ? currentFile : '';
        },
        // First argument must be the store object, however for generic use we do not know what get request
        // will be used (all campaigns or the activeCampaign), and linting states no unused variables or
        // arguments or empty objects
        async createCampaign(_context: Ctx, campaign: Campaign): Promise<unknown> {
            _context;
            const response = await axios.post('/api/campaigns', campaign);
            return response.data;
        },
        async updateCampaign(_context: Ctx, campaign: Campaign): Promise<void> {
            _context;
            await axios.put(`/api/campaigns/${campaign.id}`, campaign);
        },
        async saveEdit(_context: Ctx, campaign: Campaign): Promise<void> {
            _context;
            if (campaign.id) {
                await axios.put('/api/campaigns', campaign);
            } else {
                await axios.post('/api/campaigns', campaign);
            }
        },
    },
    getters: {
        campaigns: (state: CampaignState) => {
            return state.campaigns;
        },
    },
};

export default CampaignStore;
